/**
 * AddVeItemModal — create or edit a Value Engineering line item (Supabase).
 *
 * Opens via CustomEvent('open-add-ve-item-modal', { detail }):
 *   create: { effortId }
 *   edit:   { effortId, itemId, prefill: { title, description, design_impact, estimated_savings, notes, disposition } }
 *
 * Busts ctx.veEffortCache + ctx.msVeCache and dispatches outline-render on success.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createItem, updateItem, VE_DISPOSITION_LABELS } from '../services/valueEngineering.js'
import Select from './Select.jsx'

const DISPOSITION_OPTIONS = Object.keys(VE_DISPOSITION_LABELS).map(v => ({ value: v, label: VE_DISPOSITION_LABELS[v] }))

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const areaStyle = { ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }

export default function AddVeItemModal() {
  const [open, setOpen]             = useState(false)
  const [effortId, setEffortId]     = useState(null)
  const [editId, setEditId]         = useState(null)
  const [title, setTitle]           = useState('')
  const [description, setDescription] = useState('')
  const [designImpact, setDesignImpact] = useState('')
  const [savings, setSavings]       = useState('')
  const [notes, setNotes]           = useState('')
  const [disposition, setDisposition] = useState('drafting')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setEffortId(d.effortId || null)
      setEditId(d.itemId || null)
      const p = d.prefill || {}
      setTitle(p.title || '')
      setDescription(p.description || '')
      setDesignImpact(p.design_impact || '')
      setSavings(p.estimated_savings != null ? String(p.estimated_savings) : '')
      setNotes(p.notes || '')
      setDisposition(p.disposition || 'drafting')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-ve-item-modal', onOpen)
    return () => window.removeEventListener('open-add-ve-item-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      title: title || null,
      description: description || null,
      design_impact: designImpact || null,
      estimated_savings: savings ? Number(savings) : null,
      notes: notes || null,
      disposition,
    }
    try {
      if (editId) await updateItem(editId, fields)
      else {
        if (!effortId) { setError('No effort in context.'); setSubmitting(false); return }
        await createItem(effortId, fields)
      }
      ctx.veEffortCache = undefined
      if (ctx.msVeCache) ctx.msVeCache = {}
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save item'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddVeItemModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {editId ? 'Edit VE Item' : 'Add VE Item'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Crown Molding" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Estimated Savings ($)</label>
              <input type="number" style={inputStyle} value={savings} onChange={e => setSavings(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Client Disposition</label>
              <Select value={disposition} onChange={setDisposition} options={DISPOSITION_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Proposal / Description</label>
            <textarea style={areaStyle} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Design Impact</label>
            <textarea style={areaStyle} value={designImpact} onChange={e => setDesignImpact(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea style={areaStyle} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Add Item')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
